import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { JobCard } from '../../components/jobs';
import { SprayCard } from '../../components/spray';
import { ClipboardList, Clock, LineChart, Plus, Users, Warehouse } from 'lucide-react';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import jobService from '../../services/jobs';
import sprayService from '../../services/spray';
import type { JobListItem, JobStatus } from '../../types/jobs';
import type { SprayApplication } from '../../types/spray';

const STATUS_TABS: { label: string; value: JobStatus | 'all' }[] = [
  { label: 'All',         value: 'all' },
  { label: 'Created',     value: 'created' },
  { label: 'Assigned',    value: 'assigned' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed',   value: 'completed' },
];

function JobsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess: canSeeWorkforce } = useModuleLicense('farm_jobs', ['supervisor', 'creator', 'admin']);
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState<JobStatus | 'all'>('all');
  const [myJobsOnly, setMyJobsOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const [sprays, setSprays] = useState<SprayApplication[]>([]);
  const [sprayLoading, setSprayLoading] = useState(true);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const filters: any = {
        status: activeStatus === 'all' ? undefined : activeStatus,
        page,
        limit: LIMIT,
      };
      if (myJobsOnly && user?.id) filters.assigned_to = user.id;

      const res = await jobService.getJobs(filters);
      if (res.success) {
        setJobs(res.data.jobs);
        setTotal(res.data.total);
      }
    } catch (e) {
      console.error('Load jobs error', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSprays = async () => {
    setSprayLoading(true);
    try {
      const filters: any = { limit: 20 };
      // Map the active status tab to a spray status filter.
      // 'in_progress' intentionally omits a filter so that paused sprays
      // also appear; all other statuses map 1:1.
      if (activeStatus !== 'all' && activeStatus !== 'in_progress') {
        filters.status = activeStatus;
      }
      if (myJobsOnly && user?.id) filters.operator_id = user.id;

      const res = await sprayService.getApplications(filters);
      if (res.success) {
        const all: SprayApplication[] = (res as any).applications ?? (res as any).data ?? [];
        // When showing "In Progress" tab include paused sprays; exclude cancelled
        const filtered = activeStatus === 'in_progress'
          ? all.filter(s => s.status === 'in_progress' || s.status === 'paused')
          : all.filter(s => s.status !== 'cancelled');
        setSprays(filtered);
      }
    } catch {
      // Silently ignore — user may not have the spray module or license
      setSprays([]);
    } finally {
      setSprayLoading(false);
    }
  };

  useEffect(() => { setPage(1); }, [activeStatus, myJobsOnly]);
  useEffect(() => { loadJobs(); }, [activeStatus, myJobsOnly, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSprays(); }, [activeStatus, myJobsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async (id: number) => {
    navigate(`/jobs/${id}/operate`);
  };

  const handleSprayStart = async (id: number) => {
    const res = await sprayService.startApplication(id);
    if (res.success) {
      navigate(`/spray/${id}`);
    } else {
      navigate(`/spray/${id}`);
    }
  };

  const handleSprayResume = async (id: number) => {
    const res = await sprayService.resumeApplication(id);
    if (res.success) {
      navigate(`/spray/${id}`);
    } else {
      navigate(`/spray/${id}`);
    }
  };

  const handleSprayComplete = (id: number) => {
    navigate(`/spray/${id}#complete`);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <PageHeader
        title="Farm Jobs"
        subtitle="Schedule and track farm operations"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            {canSeeWorkforce && (
              <Button variant="outline" size="sm" onClick={() => navigate('/harvest/shed')}>
                <Warehouse className="h-4 w-4" />
                Shed
              </Button>
            )}
            {canSeeWorkforce && (
              <Button variant="outline" size="sm" onClick={() => navigate('/jobs/insights')}>
                <LineChart className="h-4 w-4" />
                Insights
              </Button>
            )}
            {canSeeWorkforce && (
              <Button variant="outline" size="sm" onClick={() => navigate('/jobs/labour')}>
                <Clock className="h-4 w-4" />
                Labour
              </Button>
            )}
            {canSeeWorkforce && (
              <Button variant="outline" size="sm" onClick={() => navigate('/jobs/workforce')}>
                <Users className="h-4 w-4" />
                Workforce
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => navigate('/jobs/new')}>
              <Plus className="h-4 w-4" />
              New Job
            </Button>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-neutral-200 flex-wrap">
          {/* Status tabs — five of them, ~320px of buttons all told, so they
              have to be allowed to wrap on a phone. */}
          <div className="flex flex-wrap items-center gap-1">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveStatus(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                activeStatus === tab.value
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* My jobs toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto">
            <input
              type="checkbox"
              checked={myJobsOnly}
              onChange={e => setMyJobsOnly(e.target.checked)}
              className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
            />
            <span className="text-neutral-600">My jobs only</span>
          </label>
        </div>

        {/* Spray Applications section — shown when the user has assigned sprays */}
        {!sprayLoading && sprays.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Spray Applications</h2>
              <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {sprays.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sprays.map(spray => (
                <SprayCard
                  key={spray.id}
                  spray={spray}
                  currentUserId={user?.id}
                  onStart={handleSprayStart}
                  onResume={handleSprayResume}
                  onComplete={handleSprayComplete}
                />
              ))}
            </div>
          </div>
        )}

        {/* Farm Job cards */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            <p className="text-sm text-neutral-500 mt-2">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 && sprays.length === 0 && !sprayLoading ? (
          <div className="text-center py-12">
            <ClipboardList className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500">No jobs found</p>
            <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/jobs/new')}>
              Create First Job
            </Button>
          </div>
        ) : jobs.length === 0 && !loading ? null : (
          <>
            {sprays.length > 0 && (
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Farm Jobs</h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  currentUserId={user?.id}
                  onStart={handleStart}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-neutral-500">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default JobsDashboard;
